import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TableMetadata,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchMinistryCommittee } from "../../api/Committee/MinistryCommitteeApi";
import { ContentArea, ListTable, ToolbarTop } from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  MinistryCommittee,
  CommitteeSearch,
  CommitteeSort,
  CommitteeStatus,
  CommitteeTypeEnum,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { ClaimType } from "../../models/User";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import {
  ministryCommitteeDetailUrl,
  ministryCommitteeEditUrl,
} from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const storageKey = "MinistryCommitteeSearchTableState";

export function MinistryCommitteeList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  const INITIAL_TABLE_STATE = useMemo(
    () =>
      TableUtils.CreateInitialTableState<CommitteeSearch>(
        storageKey,
        TableUtils.tableDefaultState()
      ),
    []
  );
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<MinistryCommittee>();
  const [tableData, setTableData] =
    useState<PagedResult<MinistryCommittee>>(defaultTableData);

  const INITIAL_COMMITTEE_SEARCH = useMemo(
    (): CommitteeSearch => ({
      committeeTypeId: CommitteeTypeEnum.Ministry,
    }),
    []
  );

  //TABLE METADATA
  const tableMetadata: TableMetadata<MinistryCommittee> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "dateFromDateTo",
        header: t("common.expertCommission.dateFromDateTo"),
        accessor: (item: MinistryCommittee) => {
          return (
            <div>
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </div>
          );
        },
        sortBy: CommitteeSort.DateFrom,
      },
      {
        columnKey: "status",
        header: t("common.status"),
        accessor: (item: MinistryCommittee) => {
          return (
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          );
        },
        sortBy: CommitteeSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: MinistryCommittee) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState] = useTable<CommitteeSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  //FILTER DATA
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    (async () => {
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<CommitteeSearch>(
        INITIAL_COMMITTEE_SEARCH,
        tableState
      );
      const itemList = await searchMinistryCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleNewExpertCommissionClicked = () => {
    TableUtils.SetSessionStorageItem<CommitteeSearch>(
      storageKey,
      INITIAL_COMMITTEE_SEARCH,
      tableState
    );
    history.push(ministryCommitteeEditUrl(0));
  };
  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        INITIAL_COMMITTEE_SEARCH,
        tableState
      );
      history.push(ministryCommitteeDetailUrl(id!));
    }
  };

  if (auth.currentRole?.claimType !== ClaimType.Global) return null;
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={
          tableData.results?.find(
            (val) =>
              val.statusId === CommitteeStatus.NotDefined ||
              val.statusId === CommitteeStatus.ConfirmationRequired
          ) === undefined && isEditable
            ? "show"
            : "disable"
        }
        showBack={"hide"}
        onClickAdd={handleNewExpertCommissionClicked}
        pageTitle={t("common.expertCommission.ministryPageName")}
      />
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
